import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { api } from "./api";
import type { AuthUser, SystemStatus } from "./types";
import { MeetingsPage } from "./pages/MeetingsPage";
import { MeetingFormPage } from "./pages/MeetingFormPage";
import { MeetingDetailPage } from "./pages/MeetingDetailPage";
import { PeoplePage } from "./pages/PeoplePage";
import { PipelinePage } from "./pages/PipelinePage";
import { ReportPage } from "./pages/ReportPage";
import { SystemPage } from "./pages/SystemPage";
import { UsersPage } from "./pages/UsersPage";
import { LoginPage } from "./pages/LoginPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { TelegramPage } from "./pages/TelegramPage";
import { LeadsPage } from "./pages/LeadsPage";
import { SetupBanner } from "./components/SetupBanner";
import { initials } from "./components/util";
import {
  IconBadge,
  IconCalendar,
  IconChevron,
  IconKey,
  IconLead,
  IconLogo,
  IconLogout,
  IconPulse,
  IconSend,
  IconUsers,
  IconBoard,
  IconChart,
} from "./components/icons";

type AuthState =
  | { status: "loading" }
  | { status: "out" }
  | { status: "in"; user: AuthUser };

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  // Кто вошёл — спрашиваем у сервера: кука httpOnly, из JavaScript её не видно
  useEffect(() => {
    api
      .me()
      .then((user) => setAuth({ status: "in", user }))
      .catch(() => setAuth({ status: "out" }));
  }, []);

  // Сессия истекла или администратор закрыл доступ прямо сейчас
  useEffect(() => {
    const onSignedOut = () => setAuth({ status: "out" });
    window.addEventListener("zmh:signed-out", onSignedOut);
    return () => window.removeEventListener("zmh:signed-out", onSignedOut);
  }, []);

  const signOut = useCallback(() => {
    api.logout().finally(() => setAuth({ status: "out" }));
  }, []);

  if (auth.status === "loading") {
    return (
      <div className="auth-screen">
        <div className="muted row">
          <span className="spinner" /> Загружаю…
        </div>
      </div>
    );
  }

  if (auth.status === "out") {
    return <LoginPage onSignedIn={(user) => setAuth({ status: "in", user })} />;
  }

  if (auth.user.must_change_password) {
    return (
      <ChangePasswordPage
        user={auth.user}
        onDone={(user) => setAuth({ status: "in", user })}
        onSignOut={signOut}
      />
    );
  }

  return <Workspace user={auth.user} onSignOut={signOut} />;
}

function greetingFor(name: string): string {
  const h = new Date().getHours();
  const part =
    h < 5
      ? "Доброй ночи"
      : h < 12
        ? "Доброе утро"
        : h < 18
          ? "Добрый день"
          : "Добрый вечер";
  return `${part}, ${name.split(/\s+/)[0]}!`;
}

function today(): string {
  return new Date().toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function Workspace({
  user,
  onSignOut,
}: {
  user: AuthUser;
  onSignOut: () => void;
}) {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const refreshStatus = useCallback(() => {
    api.status().then(setStatus).catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 20_000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  const isAdmin = user.role === "admin";
  const ownLeadsOnly = !isAdmin && user.own_leads_only;

  return (
    <div className="shell">
      <aside className="sidebar">
        <NavLink to="/leads" className="logo">
          <span className="logo-mark">
            <IconLogo />
          </span>
          <span className="label">ELG Meeting Hub</span>
        </NavLink>

        <nav className="side-nav">
          <NavLink to="/leads" className="side-link">
            <IconLead />
            <span className="label">Лиды</span>
          </NavLink>
          <NavLink to="/pipeline" className="side-link">
            <IconBoard />
            <span className="label">Воронка</span>
          </NavLink>
          <NavLink to="/report" className="side-link">
            <IconChart />
            <span className="label">Отчёт</span>
          </NavLink>
          <NavLink to="/meetings" className="side-link">
            <IconCalendar />
            <span className="label">Встречи</span>
          </NavLink>
          {/* В справочнике есть и клиенты — тому, кто видит только своих, он не нужен */}
          {!ownLeadsOnly && (
            <NavLink to="/people" className="side-link">
              <IconUsers />
              <span className="label">Люди</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/staff" className="side-link">
              <IconBadge />
              <span className="label">Сотрудники</span>
            </NavLink>
          )}
          <NavLink to="/telegram" className="side-link">
            <IconSend />
            <span className="label">Напоминания</span>
          </NavLink>
          <NavLink to="/system" className="side-link">
            <IconPulse />
            <span className="label">Состояние</span>
          </NavLink>
        </nav>

        <div className="side-foot">
          <button className="side-link danger-link" onClick={onSignOut}>
            <IconLogout />
            <span className="label">Выйти</span>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="greeting">
            <strong>{greetingFor(user.name)}</strong>
            <span>{today()}</span>
          </div>

          {status && (
            <div className="row hide-narrow">
              <span
                className={`badge ${status.ai.configured ? "green" : "amber"}`}
                title={
                  status.ai.configured
                    ? `Модель: ${status.ai.model}`
                    : "ANTHROPIC_API_KEY не задан"
                }
              >
                <span className="dot" /> Claude
              </span>
              <span
                className={`badge ${status.telegram.configured ? "green" : "amber"}`}
                title={
                  status.telegram.configured
                    ? `Напоминание за ${status.telegram.reminderLeadMinutes} мин, привязано: ${status.telegram.linkedUsers}`
                    : "TELEGRAM_BOT_TOKEN не задан"
                }
              >
                <span className="dot" /> Телеграм
              </span>
              <span
                className={`badge ${status.zoom.configured ? "green" : ""}`}
                title={
                  status.zoom.configured
                    ? `Хост: ${status.zoom.hostEmail}`
                    : "Ключи Zoom не заданы — для очных встреч они и не нужны"
                }
              >
                <span className="dot" /> Zoom
              </span>
            </div>
          )}

          <div className="user-menu">
            <button className="user-btn" onClick={() => setMenuOpen((v) => !v)}>
              <span className="avatar">{initials(user.name)}</span>
              <span className="hide-narrow">{user.name}</span>
              <IconChevron />
            </button>
            {menuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
                <div className="menu">
                  <div className="menu-head">
                    <strong>{user.name}</strong>
                    <div className="muted small mono">{user.login}</div>
                    <div className="muted small">
                      {isAdmin ? "администратор" : "сотрудник"}
                    </div>
                  </div>
                  <button
                    className="menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/telegram");
                    }}
                  >
                    Напоминания в телеграме
                  </button>
                  <button
                    className="menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/password");
                    }}
                  >
                    Сменить пароль
                  </button>
                  <button
                    className="menu-item danger"
                    onClick={() => {
                      setMenuOpen(false);
                      onSignOut();
                    }}
                  >
                    Выйти
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="content">
          <SetupBanner status={status} isAdmin={isAdmin} />
          <Routes>
            <Route path="/" element={<Navigate to="/leads" replace />} />
            <Route path="/meetings" element={<MeetingsPage status={status} />} />
            <Route path="/meetings/new" element={<MeetingFormPage status={status} />} />
            <Route
              path="/meetings/:id/edit"
              element={<MeetingFormPage status={status} />}
            />
            <Route
              path="/meetings/:id"
              element={
                <MeetingDetailPage
                  status={status}
                  onChanged={refreshStatus}
                  isAdmin={isAdmin}
                />
              }
            />
            <Route path="/people" element={<PeoplePage />} />
          <Route path="/leads" element={<LeadsPage />} />
            <Route path="/pipeline" element={<PipelinePage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route
              path="/staff"
              element={
                isAdmin ? (
                  <UsersPage me={user} status={status} />
                ) : (
                  <div className="card">
                    <div className="empty">
                      <h3>Раздел доступен только администратору</h3>
                      <p>Обратитесь к тому, кто выдал вам доступ.</p>
                    </div>
                  </div>
                )
              }
            />
            <Route path="/telegram" element={<TelegramPage />} />
            <Route
              path="/password"
              element={<SelfPasswordPage onDone={() => navigate("/meetings")} />}
            />
            <Route
              path="/system"
              element={<SystemPage onRefresh={refreshStatus} isAdmin={isAdmin} />}
            />
            <Route
              path="*"
              element={
                <div className="card">
                  <div className="empty">
                    <h3>Страница не найдена</h3>
                  </div>
                </div>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/** Добровольная смена своего пароля из меню — в отличие от принудительной. */
function SelfPasswordPage({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== repeat) {
      setError("Новый пароль и подтверждение не совпадают");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.changePassword(current, next);
      setDone(true);
      setTimeout(onDone, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Смена пароля</h1>
          <p>Все остальные ваши сеансы после смены будут завершены.</p>
        </div>
      </div>

      {done && <div className="banner green-banner">Пароль изменён.</div>}
      {error && <div className="banner red">{error}</div>}

      <div className="card" style={{ maxWidth: 480 }}>
        <div className="card-head">
          <h3 className="row">
            <IconKey /> Новый пароль
          </h3>
        </div>
        <div className="card-body">
          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="sp-cur">Текущий пароль</label>
              <input
                id="sp-cur"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="sp-new">Новый пароль</label>
              <input
                id="sp-new"
                type="password"
                autoComplete="new-password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              <div className="muted small" style={{ marginTop: 6 }}>
                Не короче 8 символов.
              </div>
            </div>
            <div className="field">
              <label htmlFor="sp-rep">Ещё раз новый пароль</label>
              <input
                id="sp-rep"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
            </div>
            <button className="primary" type="submit" disabled={busy || done}>
              {busy && <span className="spinner" />} Сохранить
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
